import React from 'react';
import styled from 'styled-components';
import { Skill } from '../types';
import { Container, Section, SectionTitle, Card, Grid } from '../styles/GlobalStyles';
import { useLanguage } from '../contexts/LanguageContext';

const AboutContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutText = styled.div`
  h3 {
    font-size: 1.8rem;
    color: #2c3e50;
    margin-bottom: 1.5rem;
  }

  p {
    font-size: 1.1rem;
    line-height: 1.8;
    color: #666;
    margin-bottom: 1.5rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const SkillsGrid = styled(Grid)`
  grid-template-columns: 1fr;
  gap: 1.5rem;
`;

const SkillCategory = styled(Card)`
  padding: 2rem;
  
  h4 {
    font-size: 1.3rem;
    color: #2c3e50;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    
    &::before {
      content: '';
      width: 4px;
      height: 20px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border-radius: 2px;
    }
  }
`;

const SkillList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const SkillTag = styled.span`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(102, 126, 234, 0.3);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const StatCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  h4 {
    font-size: 2.5rem;
    color: #667eea;
    margin-bottom: 0.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem 0;
  }
  
  p {
    color: #666;
    font-weight: 500;
    text-transform: uppercase;
    font-size: 0.9rem;
    letter-spacing: 1px;
    margin: 0;
    text-align: center;
    line-height: 1.3;
  }
`;

interface AboutProps {
  skills: Skill[];
}

const About: React.FC<AboutProps> = ({ skills }) => {
  const { t, language } = useLanguage();
  
  // Récupérer la description comme array depuis les traductions
  const getDescriptionParagraphs = () => {
    const translations = {
      fr: [
        'Développeur web passionné, je me suis découvert une véritable fascination pour la création d\'applications et de sites web modernes.',
        'Après avoir obtenu mon diplôme d\'ingénieur informatique, je continue d\'apprendre et de me perfectionner dans les technologies front-end et back-end.',
        'Ma curiosité naturelle et ma rigueur me poussent à toujours chercher les meilleures solutions techniques tout en gardant l\'expérience utilisateur au cœur de mes préoccupations.',
        'Je suis à la recherche d\'opportunités pour mettre en pratique mes compétences et contribuer à des projets stimulants au sein d\'une équipe dynamique.'
      ],
      en: [
        'Passionate web developer, I discovered a true fascination for creating modern applications and websites.',
        'After obtaining my computer engineering degree, I continue to learn and improve my skills in front-end and back-end technologies.',
        'My natural curiosity and rigor drive me to always seek the best technical solutions while keeping user experience at the heart of my concerns.',
        'I am looking for opportunities to put my skills into practice and contribute to stimulating projects within a dynamic team.'
      ]
    };
    return translations[language];
  };
  
  // Statistiques traduites
  const stats = [
    { number: "3+", label: t('about.stats.experience') || "Années d'expérience" },
    { number: "10+", label: t('about.stats.projects') || "Projets réalisés" },
    { number: "30+", label: t('about.stats.technologies') || "Technologies" },
  ];

  return (
    <Section id="about">
      <Container>
        <SectionTitle>{t('about.title')}</SectionTitle>
        
        <AboutContent>
          <AboutText>
            <h3>{t('about.subtitle')}</h3>
            {getDescriptionParagraphs().map((paragraph: string, index: number) => (
              <p key={index}>{paragraph}</p>
            ))}
            
            <StatsGrid>
              {stats.map((stat, index) => (
                <StatCard key={index}>
                  <h4>{stat.number}</h4>
                  <p>{stat.label}</p>
                </StatCard>
              ))}
            </StatsGrid>
          </AboutText>

          <div>
            <h3 style={{ marginBottom: '2rem', color: '#2c3e50' }}>{t('about.skills')}</h3>
            <SkillsGrid>
              {skills.map((skillCategory, index) => (
                <SkillCategory key={index}>
                  <h4>{skillCategory.category}</h4>
                  <SkillList>
                    {skillCategory.skills.map((skill, skillIndex) => (
                      <SkillTag key={skillIndex}>{skill}</SkillTag>
                    ))}
                  </SkillList>
                </SkillCategory>
              ))}
            </SkillsGrid>
          </div>
        </AboutContent>
      </Container>
    </Section>
  );
};

export default About;